import { useEffect } from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { useNavigate } from 'react-router';
import { FooterButton } from '@/components/common/FooterButton';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { GRID_KEYS } from '@/constants/gridKeys';
import { paths } from '@/constants/paths';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useGridColumns } from '@/hooks/useGridColumns';
import { useLocalization } from '@/hooks/useLocalization';
import type { StandardWidgetProps } from '@/models/WidgetModels';
import {
  useGetAdvisorAggregatedHoldingsQuery,
  type AdvisorAggregatedHolding,
  type GetAdvisorAggregatedHoldingsQueryArg,
} from '@/store/advisorAggregatedHoldingsApi';
import { ContentContainer } from '@/widgets/contentContainer';
import AggregatedHoldingsGrid from '../components/AggregatedHoldingsGrid';

export const TOP_HOLDINGS_LIMIT = 10;

const TOP_HOLDINGS_QUERY: GetAdvisorAggregatedHoldingsQueryArg = {
  OnlyPrimaryInvestors: false,
  page: 1,
  limit: TOP_HOLDINGS_LIMIT,
  sort: [{ property: 'Balance', direction: 'desc', order: 1 }],
};

const EMPTY_RECORDS: AdvisorAggregatedHolding[] = [];

const isEmbeddableBundle = import.meta.env.VITE_WIDGET === 'external';

const DashboardTopHoldingsWidget = ({
  title,
  onDataLoaded,
  container = 'widget',
}: StandardWidgetProps) => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const authIsLoaded = useAuthIsLoaded();
  const widgetTitle = title || t('widgetTopHoldings', 'Top Holdings');

  const holdingsQuery = useGetAdvisorAggregatedHoldingsQuery(
    authIsLoaded ? TOP_HOLDINGS_QUERY : skipToken,
    { refetchOnMountOrArgChange: true },
  );
  const columnsQuery = useGridColumns(GRID_KEYS.ADVISOR_AGGREGATED_HOLDINGS, {
    skip: !authIsLoaded,
  });

  const { currentData } = holdingsQuery;
  const isError = holdingsQuery.isError || columnsQuery.isError;
  const isSettled =
    (currentData !== undefined || holdingsQuery.isError) &&
    (columnsQuery.data !== undefined || columnsQuery.isError);

  useEffect(() => {
    if (isSettled) {
      onDataLoaded?.();
    }
  }, [isSettled, onDataLoaded]);

  const handleRetry = () => {
    if (holdingsQuery.isError) void holdingsQuery.refetch();
    if (columnsQuery.isError) void columnsQuery.refetch();
  };

  const records = currentData?.Records ?? EMPTY_RECORDS;

  return (
    <ContentContainer
      container={container}
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      <Typography variant='h5'>{widgetTitle}</Typography>
      <Divider sx={{ my: 2 }} />

      {isError ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 6,
          }}
        >
          <Typography color='error'>
            {t('errorLoadingHoldingsData', 'Error loading holdings data.')}
          </Typography>
          <Button
            variant='outlined'
            onClick={handleRetry}
          >
            {t('retry', 'Retry')}
          </Button>
        </Box>
      ) : authIsLoaded ? (
        <AggregatedHoldingsGrid
          variant='widget'
          records={records}
          loading={holdingsQuery.isFetching}
        />
      ) : (
        <LoadingSpinner />
      )}

      {!isEmbeddableBundle && (
        <FooterButton
          buttonText={t('seeAllHoldings', 'See All Holdings')}
          onClick={() => {
            void navigate(paths.advisor.clientsWealth.holdings);
          }}
        />
      )}
    </ContentContainer>
  );
};

export default DashboardTopHoldingsWidget;
